/**
 * Error types.
 */
export enum ErrorType {
  /**
   * The API was used incorrectly. This is a fatal error and won't work on retry.
   */
  BadRequest = "BadRequest", // Input to our API was wrong
  /**
   * A temporary error was encountered. Retrying may succeed.
   */
  Transient = "Transient", // Retriable error
  /**
   * The requested user scenario isn't supported. Examples include password-protected files or unsupported file types.
   * This is a fatal error and won't work on retry.
   */
  NotSupported = "NotSupported", // Scenario failed for an expected reason
  /**
   * An unexpected error was encountered within the MDCPP code. This is a fatal error and may not work on retry.
   */
  Unexpected = "Unexpected", // Scenario failed for an unxpected reason
  /**
   * The authentication token for the user was invalid, or the user doesn't have access to the content or operation.
   */
  Unauthorized = "Unauthorized", // Authentication or authorization failed
}

/**
 * Information about the external partner host.
 */
export interface HostInfo {
  /**
   * Required. The name of the Collaboration app registered with the MDCPP program.
   */
  hostName: string; // this field is a loggable string indicating the 3P host
  /**
   * Optional. This field indicates device info, like the platform (desktop, web, mobile, iOS, etc.).
   */
  hostClientType?: string;
}

/**
 * Information about the document.
 */
export interface DocumentInfo {
  /**
   * Optional. Information to uniquely identify the document.
   */
  documentIdentifier?: DocumentIdentifier; // optional, indicates a set of unique document identifiers
  /**
   * Optional. See definition of the Action enum for available actions. 'Default' option is used if no action is provided.
   */
  action?: Action; // if not provided by host, will make it default
  /**
   * Optional. The platform (desktop, web, mobile, iOS, etc.) the session is originating from.
   */
  sessionOrigin?: string; // origin of the request
  /**
   * Optional. UI locale of the current application.
   */
  uiCulture?: string;
  /**
   * Optional. Document content locale preferred for this session.
   */
  dataCulture?: string;
  /**
   * Optional. Timestamp of when the user clicks a document.
   * This will be used to measure end-to-end user experience time.
   * Example: `new Date().getTime()`
   */
  clickTime?: number;
  /**
   * Optional. Share link provided by the UI host to open the document.
   */
  shareUrl?: string; // share link provided by ui host to open document
}

// defines Action field in DocumentInfo
/**
 * Possible actions.
 */
export enum Action {
  /**
   * Open as default (edit/view) as specified by the application if not explicitly passed by UI hosts.
   *
   * For editor mode, if permission isn't met, will be changed to View by SharePoint.
   */
  Default = "Default",
  /**
   * Editor mode. If permission isn't met, will be changed to View by SharePoint.
   */
  Edit = "Edit",
  /**
   * View mode.
   */
  View = "View",
}

/**
 * Information about the session.
 */
export interface SessionInfo {
  /**
   * Required. Unique ID for this session.
   * This will be logged to Microsoft diagnostics, so it must not contain any user personal data.
   * This field should be provided for live site investigation.
   */
  hostCorrelationId: string;
  /**
   * Optional. High contrast mode preferred for this session.
   *
   * Supported values for this field are:
   *
   * - undefined
   *
   * - invert
   */
  highContrastMode?: string;

  /**
   * Optional. Indicates if the session is a test session.
   */
  isSynthetic?: boolean;
}

/**
 * Information about the meeting.
 * @alpha
 */
export interface MeetingInfo {
  /**
   * Required. Unique ID for this meeting. This should be globally unique for your application.
   * This field will be logged to Microsoft diagnostics, so it must not contain any user personal data.
   */
  id: string; // required, unique meeting Id
}

/**
 * Information about the user.
 * @alpha
 */
export interface UserInfo {
  /**
   * Required. Unique ID for the user which will remain the same across all of the user's interactions.
   */
  userId: string; //unique id from ui host, same user id for multiple sessions
  /**
   * Optional. Unique ID for the participant in the meeting.
   * For example, if a single user has joined the meeting on two devices, they should have one userId and two participantIds.
   */
  participantId?: string; // ppt specific, unique per device even if a single user is on multiple devices
  /**
   * Optional. The user's preferred name to show to other attendees in the meeting. Used by PowerPoint.
   */
  displayName?: string; // ppt presenter specific
}

/**
 * Information returned by the Meeting and Document boot flows.
 */
export interface BootInfo {
  /**
   * Required. Status of boot result.
   */
  isBootSuccess: boolean;
  /**
   * Optional. Error to be returned.
   */
  errorInfo?: ErrorInfo;
  /**
   * Optional. For Meeting experiences, the start* APIs will return this joinInfo.
   * This is a string which must be passed to the attendees for them to join the sharing session.
   * This may contain sensitive user information, so this shouldn't be logged in telemetry or diagnostics.
   */
  joinInfo?: string;
}

/**
 * Error information returned by BootInfo.errorInfo.
 */
export interface ErrorInfo {
  /**
   * Required. A string describing the error details.
   * This string will be in English and should only be used for diagnostics purposes.
   */
  errorMessage: string;
  /**
   * Optional. HTTP error code.
   */
  errorCode?: string;
  /**
   * Optional. A short name describing the error.
   */
  errorName?: string;
  /**
   * Optional. A categorization for the error. See definition of the ErrorType enum for available types.
   */
  errorType?: ErrorType;
}

// input object for document experience
/**
 * Defines the input type for the launch function.
 */
export interface BootDocumentParams {
  /**
   * Required. Information about the document. See definition of DocumentInfo for details.
   */
  documentInfo: DocumentInfo;
  /**
   * Required. Information about the session. See definition of SessionInfo for details.
   */
  sessionInfo: SessionInfo;
  /**
   * Required. The HTML element for the document to be viewed in.
   */
  container: HTMLElement;
  /**
   * Required. Callback used to provide an access token for accessing the document.
   *
   * @param resourceUrl - URL for the document to be accessed.
   * @param claim - Additional hash used to authenticate the user.
   * @returns The access token.
   */
  fetchAccessToken: (resourceUrl: string, claim?: string[]) => Promise<string>;
  /**
   * Optional. Callback for event handling.
   */
  eventHandlers?: UiHostEventHandlers;
}

// input object for collaboration meeting experience attendee
/**
 * Defines the input type for the joinCollaboration function.
 * @alpha
 */
export interface JoinCollaborationParams extends BootDocumentParams {
  /**
   * Required. Information about the collaboration meeting. See definition of MeetingInfo for details.
   */
  meetingInfo: MeetingInfo;
  /**
   * Required. Information about a collaboration meeting attendee. See definition of UserInfo for details.
   */
  userInfo: UserInfo;
}

// input object for collaboration meeting experience presenter
/**
 * Defines the input type for the startCollaboration function.
 * @alpha
 */
export interface StartCollaborationParams extends JoinCollaborationParams {}

// input object for present meeting experience
/**
 * Defines the input type for the bootPresentation function.
 * @alpha
 */
export interface BootPresentationParams {
  /**
   * Required. The HTML element to put the presentation view in.
   */
  container: HTMLElement;
  /**
   * Required. Information about the meeting. See definition of MeetingInfo for details.
   */
  meetingInfo: MeetingInfo;
  /**
   * Required. Information about the presenter. See definition of UserInfo for details.
   */
  userInfo: UserInfo;
  /**
   * Required. Information about the session. See definition of SessionInfo for details.
   */
  sessionInfo: SessionInfo;
  /**
   * Optional. Callback for event handling.
   */
  eventHandlers?: UiHostEventHandlers;
}

// input object for present meeting experience presenter
/**
 * The input type for the startPresentation function.
 * @alpha
 */
export interface StartPresentationParams extends BootPresentationParams {
  /**
   * Required. Information about the document. See definition of DocumentInfo for details.
   */
  documentInfo: DocumentInfo;
  /**
   * Required. Callback used to provide an access token for accessing the document.
   *
   * @param resourceUrl - URL for the document to be accessed.
   * @param claim - Additional hash used to authenticate the user.
   * @returns The access token.
   */
  fetchAccessToken: (resourceUrl: string, claim?: string[]) => Promise<string>;
}

// input object for present meeting experience attendee
/**
 * The input type for the joinPresentation function.
 * @alpha
 */
export interface JoinPresentationParams extends BootPresentationParams {
  /**
   * Required. The joinInfo received from the presenter's boot response.
   */
  joinInfo: string;
}

/**
 * The callback for event handling.
 */
export interface UiHostEventHandlers {
  /**
   * Optional. Callback used to notify the host if an error is encountered after booting the experience.
   * Suggested use: Log diagnostic information and show an error page to the user if the error is fatal.
   *
   * @param error - Error information. See definition of ErrorInfo for details.
   */
  onError?: (error: ErrorInfo) => void;
  // notify ui host focus is to be returned from wac frame
  /**
   * Optional. Callback used to notify host that focus is returned to the host.
   *
   * @param navigateForward - Indicates the keyboard focus direction when the user switches from the Office app iframe
   * back to the external partner host UI frame. `true` represents clockwise, otherwise `false`.
   */
  onReturnFocus?: (navigateForward?: boolean) => void;
  // session end callback from ui host
  /**
   * Optional. Callback used to notify the host that the user has closed the document.
   * Suggested use: Clean up resources and logging telemetry.
   */
  onDocumentClose?: () => void;
}

/**
 * Host client types.
 */
export enum HostClientType {
  /**
   * Desktop client. For example, Windows, Mac.
   */
  desktop = "desktop",
  /**
   * Web browser client.
   */
  web = "web",
  /**
   * Android client.
   */
  android = "android",
  /**
   * iOS client.
   */
  ios = "ios",
}

// Unique document identifiers required during booting process in all scenarios except shareUrl open document
/**
 * Information to uniquely identify a document.
 */
export interface DocumentIdentifier {
  /**
   * Required. The domain or site URL of the document.
   */
  siteUrl: string; // domain url of the document
  /**
   * Required. The unique document ID or relative path of the document.
   *
   * If using the OneDrive Picker API, use the field `sharepointIds.listItemUniqueId`.
   */
  sourceDoc: string; // unique document Id or relative path of a document
}
